import { TeamName } from "@/components/TeamName";
import { AppointmentStatusIndicator } from "@/features/appointments/AppointmentStatusIndicator";
import { getRelativeUrl } from "@/utils/getRelativeUrl";
import { useAppointmentsSearch } from "@repo/api";
import { ListView, Loading, Text, calendarIcon } from "@repo/skapa";
import { t } from "@repo/translation";
import {
  CoWorkerName,
  DebouncedSearchField,
  ElementsContainer,
  ListViewItemWithActions,
  ListViewItemWithActionsCheckbox,
  LocalizedDateTimeRange,
  Name,
  OneLineInfo,
  SectionContainer,
  translateServices,
} from "@repo/ui";
import { hasItems, isDefined } from "@repo/utils";

import {
  type SearchAppointmentState,
  type SearchAppointmentStateActions,
} from "./SearchAppointmentReducer";

const searchOptions = {
  sortKey: "timeWindowStartTime",
  sortOrder: "asc" as const,
  pageSize: 100,
  pageIndex: 0,
};
interface SearchAppointmentsProps {
  searchTerm?: string;
  state: SearchAppointmentState;
  actions: SearchAppointmentStateActions;
  closeModal: () => void;
}

export const SearchAppointments = ({
  searchTerm,
  state,
  actions,
}: SearchAppointmentsProps) => {
  const { isValidating, data } = useAppointmentsSearch(
    searchTerm ? { searchTerm } : undefined,
    searchOptions,
    false
  );

  const results =
    data
      ?.flatMap((d) => d.content)
      .sort((a, b) => {
        return (
          new Date(b.timeWindowStartTime).getTime() -
          new Date(a.timeWindowStartTime).getTime()
        );
      }) ?? [];

  return (
    <SectionContainer>
      <DebouncedSearchField
        id="seach-appointment-input"
        placeholder={t("action.placeholder_search")}
        debounceTime={700}
        value={searchTerm}
        onChange={(term) => {
          actions.setSearchValue(term);
        }}
      />
      {isValidating && <Loading text={t("prompt.loading")} />}
      {hasItems(results) && (
        <ListView id="search-result">
          {results?.map((app) => (
            <ListViewItemWithActions
              key={app.appointmentId}
              icon={calendarIcon}
              title={
                <Text textStyle="Heading.XS">
                  <LocalizedDateTimeRange
                    fromDate={app.timeWindowStartTime}
                    toDate={app.timeWindowEndTime}
                  />
                </Text>
              }
              description={
                <ElementsContainer>
                  <div>
                    <Text>
                      <Name
                        firstName={app.customer.firstName}
                        lastName={app.customer.lastName}
                      />
                    </Text>
                    <OneLineInfo
                      small
                      info={[
                        translateServices(app.service),
                        app.locationName,
                      ].filter(isDefined)}
                    />
                    <OneLineInfo
                      small
                      info={[
                        app.assignedCoworker ? (
                          <CoWorkerName email={app.assignedCoworker} fragment />
                        ) : app.assignedTeam ? (
                          <TeamName teamId={app.assignedTeam} fragment />
                        ) : (
                          t("label.unassigned")
                        ),
                        app.orderNumber !== undefined
                          ? String(app.orderNumber)
                          : undefined,
                      ].filter(isDefined)}
                    />
                    <AppointmentStatusIndicator
                      status={app.appointmentStatus}
                    />
                  </div>
                  {app.opportunityId && (
                    <div>
                      <Text textStyle="Body.M">
                        {t("label.currently_associated_to")}:
                      </Text>
                      <a
                        href={getRelativeUrl(
                          `/opportunity/${app.opportunityId}`
                        )}
                      >
                        {app.opportunityTitle ?? app.opportunityId}
                      </a>
                    </div>
                  )}
                </ElementsContainer>
              }
              action={
                <ListViewItemWithActionsCheckbox
                  subtle
                  label={`${app.customer.firstName} ${app.customer.lastName}`}
                  id={`checkbox-${app.id}`}
                  value={String(app.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    actions.setSelectedAppointment(
                      e.target.checked ? app : undefined
                    );
                  }}
                  checked={
                    state.selectedAppointment?.id === app.id ||
                    state.initiallySelectedAppointments?.includes(
                      String(app.id)
                    )
                  }
                  disabled={state.initiallySelectedAppointments?.includes(
                    String(app.id)
                  )}
                />
              }
            />
          ))}
        </ListView>
      )}
      {!isValidating && !hasItems(results) && (
        <Text>
          {t("prompt.no_result_for", { searchTerm: state?.searchValue })}
        </Text>
      )}
    </SectionContainer>
  );
};
